import React, { FC, PropsWithChildren, ReactNode, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { throttle } from "lodash";

import "./Tooltip.scss";

export interface DynamicTooltipProps extends PropsWithChildren {
  content: ReactNode;
}

export const DynamicTooltip: FC<DynamicTooltipProps> = ({ content, children }) => {
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const [tooltipPosition, setTooltipPosition] = useState(() => ({ x: 0, y: 0 }));

  const handleUpdateTooltipPosition = throttle(({ x, y }: { x: number; y: number }) => {
    const offset = 12;

    setTooltipPosition({ x: x + offset, y: y + offset });
  }, 16);

  const handleMouseEnter = useCallback(
    (evt: React.MouseEvent) => {
      handleUpdateTooltipPosition({
        x: evt.clientX,
        y: evt.clientY,
      });

      setTooltipVisible(true);
    },
    [handleUpdateTooltipPosition],
  );

  const handleMouseLeave = useCallback(() => {
    setTooltipVisible(false);
  }, []);

  const handleMouseMove = useCallback(
    (evt: React.MouseEvent) => {
      handleUpdateTooltipPosition({
        x: evt.clientX,
        y: evt.clientY,
      });
    },
    [handleUpdateTooltipPosition],
  );

  const tooltipPortal = tooltipVisible
    ? createPortal(
        <div
          className="tooltip-content dynamic"
          style={{
            position: "fixed",
            left: tooltipPosition.x,
            top: tooltipPosition.y,
          }}
        >
          {content}
        </div>,
        document.body,
      )
    : null;

  if (!content) return <>{children}</>;

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onMouseMove={handleMouseMove}>
      {children}
      {tooltipPortal}
    </div>
  );
};
